import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export function normalizeIban(value: string | null | undefined): string {
    return String(value ?? '').replace(/\s+/g, '').toUpperCase().trim();
}

// This method is used to custom validator using type and country code
export function customValidator(
    type:
        | 'english'
        | 'arabic'
        | 'email'
        | 'cardNumber'
        | 'cvv'
        | 'mobile'
        | 'landline'
        | 'emiratesId'
        | 'websiteLink'
        | 'uaeMobile'
        | 'uaeLandline'
        | 'numberOnly'
        | 'numberWithDecimal'
        | 'noSpecialChars'
        | 'noTags'
        | 'safeText'
        | 'uaePhoneNumber'
        | 'customSafeText'
        | 'iban',
    countryCode?: string // Pass 'UAE' when relevant
): ValidatorFn {
    const validatorFn: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        let value = control.value;

        if (!value) return null;
        //Trim first
        value = value.toString().trim();
        //If only spaces, treat as invalid
        if (value.length === 0) {
            return { whitespace: true };
        }

        const patterns: Record<string, RegExp> = {
            email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            english: /^[a-zA-Z0-9\s\-,.&()']+$/,
            arabic: /^(?=.*[\u0600-\u06FF])[a-zA-Z\u0600-\u06FF0-9\s\-.,&()'"]+$/,
            cardNumber: /^[0-9 ]{15,19}$/,
            cvv: /^[0-9]{3,4}$/,
            mobile: /^[0-9]{7,15}$/,
            websiteLink: /^(https?:\/\/)?([\w\-]+\.)+[a-z]{2,6}(:\d+)?(\/[^\s]*)?$/,
            uaeMobile: /^5\d{8}$/,    // Starts with 5, total 9 digits
            uaeLandline: /^4\d{7}$/,   // Starts with 4, total 8 digits
            numberOnly: /^\d+$/,
            numberWithDecimal: /^\d+(\.\d{1,2})?$/,
            // Arabic letters + English letters + English digits + Arabic digits
            // Allowed symbols: space - ' " ( ) . & : % / new line
            //noSpecialChars: /^(?!.*[@^*_=\~?!{}])[\u0600-\u06FFa-zA-Z0-9\u0660-\u0669 \r\n\-'"().&:%,\/#$+\\|;\[\]]+$/,
            noSpecialChars: /^(?!.*[@^*_=\~?!{}])[\u0600-\u06FFa-zA-Z0-9\u0660-\u0669 \r\n\-'"().&:%,\/#$+\\|;\[\]\u2122\uE000-\uF8FF]+$/,
            // No HTML tags allowed
            // noTags: /^(?!.*<[^>]+>).*$/,
            noTags: /^(?!.*<[^>]+>)[\s\S]*$/,
            uaePhoneNumber: /^[234679]\d{7}$/,
            //emiratesId: /^784-\d{4}-\d{7}-\d{1}$|^784\d{4}\d{7}\d{1}$/,
            customSpecialChars: /^(?!.*[@^*_=\~?!{}<>"])[\u0600-\u06FFa-zA-Z0-9\u0660-\u0669 \r\n\-'\(\)\.&:%,\/#$+\\|;\[\]]+$/,
        };
        // UAE-specific logic
        if (type === 'mobile' && countryCode === 'UAE') {
            return patterns['uaeMobile'].test(value) ? null : { uaeMobile: true };
        }

        if (type === 'landline' && countryCode === 'UAE') {
            return patterns['uaeLandline'].test(value) ? null : { uaeLandline: true };
        }
        // Combined safeText = noSpecialChars + noTags
        if (type === 'safeText') {
            const noSpecial = patterns['noSpecialChars'].test(value);
            const noTags = patterns['noTags'].test(value);
            return noSpecial && noTags ? null : { safeText: true };
        }
        if (type === 'customSafeText') {
            const noSpecial = patterns['customSpecialChars'].test(value);
            const noTags = patterns['noTags'].test(value);
            return noSpecial && noTags ? null : { customSafeText: true };
        }
        if (type === 'emiratesId') {
            const normalizedValue = value.replace(/-/g, '');
            const isFifteenDigits = /^\d{15}$/.test(normalizedValue);
            const startsWith784 = normalizedValue.startsWith('784');
            const year = Number(normalizedValue.slice(3, 7));
            const currentYear = new Date().getFullYear();
            const hasValidYear = year >= 1940 && year < currentYear;

            return isFifteenDigits && startsWith784 && hasValidYear
                ? null
                : { emiratesId: true };
        }
        if (type === 'iban') {
            const normalizedValue = normalizeIban(value);
            const hasSpaces = /\s/.test(value);
            const isPlaceholderValue = normalizedValue === 'N/A';
            const isValidIbanFormat =
                normalizedValue.length >= 15 &&
                normalizedValue.length <= 34 &&
                /^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(normalizedValue);

            return !hasSpaces && !isPlaceholderValue && isValidIbanFormat
                ? null
                : { iban: true };
        }
        // Default pattern check
        const isValid = patterns[type].test(value);
        return isValid ? null : { [type]: true };
    };

    // tag validator with its type so components can adjust validators dynamically
    (validatorFn as any).__validatorType = type;
    return validatorFn;
}
